import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Save, Eye, ShoppingBag } from 'lucide-react';
import Layout from '../../../../components/Layout';
import { customerService, CustomerDTO, UpdateCustomerRequest } from '../../../../services/customerService';
import { customerAttributeService, CustomerAttributeFieldDTO } from '../../../../services/customerAttributeService';
import CustomerAttributeFields from '../../../../components/CustomerAttributeFields';
import { useAppAccess } from '../../../../context/AppAccessContext';
import { getApiErrorMessage } from '../../../../utils/errorUtils';
import '../../../../styles/ImsTheme.css';
import '../../../../pages/Products/ProductCategoryEditPage.css';

const MAX_LENGTHS = {
  name: 150,
  contactEmail: 150,
  contactPhone: 100,
  billingAddress: 500,
  shippingAddress: 500,
  city: 100,
  state: 100,
  postalCode: 50,
  country: 100,
  taxId: 50,
};

interface FieldErrors {
  name?: string;
  contactEmail?: string;
  contactPhone?: string;
  billingAddress?: string;
  shippingAddress?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  taxId?: string;
  creditLimit?: string;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const CustomerEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentApp } = useAppAccess();
  const isCarRental = currentApp === 'carRental';
  const [customer, setCustomer] = useState<CustomerDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const [attrFields, setAttrFields] = useState<CustomerAttributeFieldDTO[]>([]);
  const [attrSetName, setAttrSetName] = useState('');
  const [attrValues, setAttrValues] = useState<Record<number, string>>({});
  const [attrErrors, setAttrErrors] = useState<Record<number, string>>({});
  const [formData, setFormData] = useState<UpdateCustomerRequest>({
    code: '',
    name: '',
    contactEmail: '',
    contactPhone: '',
    billingAddress: '',
    shippingAddress: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    taxId: '',
    creditLimit: undefined,
    paymentTerms: '',
    customerType: '',
    isActive: true,
  });

  useEffect(() => {
    loadCustomer();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!isCarRental || !id) return;
    customerAttributeService.getForCustomer(parseInt(id), 'carRental')
      .then(res => {
        const d = (res.data as any)?.data ?? res.data;
        setAttrFields(d?.attributes ?? []);
        setAttrSetName(d?.setName ?? t('customer.driverDetails', 'Driver Details'));
        const vals: Record<number, string> = {};
        (d?.attributes ?? []).forEach((f: CustomerAttributeFieldDTO) => {
          if (f.value != null) vals[f.attributeDefinitionId] = f.value;
        });
        setAttrValues(vals);
      })
      .catch(() => {});
  }, [isCarRental, id, t]);

  const handleAttrChange = (attributeDefinitionId: number, value: string) => {
    setAttrValues(prev => ({ ...prev, [attributeDefinitionId]: value }));
    setAttrErrors(prev => ({ ...prev, [attributeDefinitionId]: '' }));
  };

  const loadCustomer = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await customerService.getCustomerById(id || '0');
      if (response?.data?.data) {
        const cust = response.data.data;
        setCustomer(cust);
        setFormData({
          code: cust.code,
          name: cust.name,
          contactEmail: cust.contactEmail,
          contactPhone: cust.contactPhone || '',
          billingAddress: cust.billingAddress || '',
          shippingAddress: cust.shippingAddress || '',
          city: cust.city || '',
          state: cust.state || '',
          postalCode: cust.postalCode || '',
          country: cust.country || '',
          taxId: cust.taxId || '',
          creditLimit: cust.creditLimit,
          paymentTerms: cust.paymentTerms || '',
          customerType: cust.customerType || '',
          isActive: cust.isActive,
        });
      }
      setLoading(false);
    } catch {
      setError(t('customer.failedToLoadCustomer'));
      setLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    if (name === 'isActive') {
      setFormData((prev) => ({ ...prev, isActive: value === 'true' }));
      return;
    }

    if (name === 'creditLimit') {
      const parsed = parseFloat(value);
      setFormData((prev) => ({ ...prev, creditLimit: isNaN(parsed) ? undefined : parsed }));
      setFieldErrors((prev) => ({ ...prev, creditLimit: undefined }));
      return;
    }

    let finalValue = value;
    if (name in MAX_LENGTHS && value.length > MAX_LENGTHS[name as keyof typeof MAX_LENGTHS]) {
      finalValue = value.substring(0, MAX_LENGTHS[name as keyof typeof MAX_LENGTHS]);
    }

    setFormData((prev) => ({ ...prev, [name]: finalValue }));
    setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validateForm = (): boolean => {
    const errors: FieldErrors = {};
    let isValid = true;

    // Name — required + max length
    if (!formData.name.trim()) {
      errors.name = t('customer.formValidation.nameRequired');
      isValid = false;
    } else if (formData.name.length > MAX_LENGTHS.name) {
      errors.name = t('customer.formValidation.nameMaxLength');
      isValid = false;
    }

    // Contact email — optional; validate format + max length only when provided
    if (formData.contactEmail.trim()) {
      if (!EMAIL_REGEX.test(formData.contactEmail)) {
        errors.contactEmail = t('customer.formValidation.emailInvalid');
        isValid = false;
      } else if (formData.contactEmail.length > MAX_LENGTHS.contactEmail) {
        errors.contactEmail = t('customer.formValidation.emailMaxLength');
        isValid = false;
      }
    }

    // Contact phone — optional, max length only
    if (formData.contactPhone && formData.contactPhone.length > MAX_LENGTHS.contactPhone) {
      errors.contactPhone = t('customer.formValidation.contactPhoneMaxLength');
      isValid = false;
    }

    // Billing address — optional, max length only
    if (formData.billingAddress && formData.billingAddress.length > MAX_LENGTHS.billingAddress) {
      errors.billingAddress = t('customer.formValidation.billingAddressMaxLength');
      isValid = false;
    }

    // Shipping address — optional, max length only
    if (formData.shippingAddress && formData.shippingAddress.length > MAX_LENGTHS.shippingAddress) {
      errors.shippingAddress = t('customer.formValidation.shippingAddressMaxLength');
      isValid = false;
    }

    // City — optional, max length only
    if (formData.city && formData.city.length > MAX_LENGTHS.city) {
      errors.city = t('customer.formValidation.cityMaxLength');
      isValid = false;
    }

    // State — optional, max length only
    if (formData.state && formData.state.length > MAX_LENGTHS.state) {
      errors.state = t('customer.formValidation.stateMaxLength');
      isValid = false;
    }

    // Postal code — optional, max length only
    if (formData.postalCode && formData.postalCode.length > MAX_LENGTHS.postalCode) {
      errors.postalCode = t('customer.formValidation.postalCodeMaxLength');
      isValid = false;
    }

    // Country — optional, max length only
    if (formData.country && formData.country.length > MAX_LENGTHS.country) {
      errors.country = t('customer.formValidation.countryMaxLength');
      isValid = false;
    }

    // Tax ID — optional, max length only
    if (formData.taxId && formData.taxId.length > MAX_LENGTHS.taxId) {
      errors.taxId = t('customer.formValidation.taxIdMaxLength');
      isValid = false;
    }

    // Credit limit — optional, must be >= 0
    if (formData.creditLimit !== undefined && formData.creditLimit !== null && formData.creditLimit < 0) {
      errors.creditLimit = t('customer.formValidation.creditLimitNegative');
      isValid = false;
    }

    setFieldErrors(errors);

    const attrErrs: Record<number, string> = {};
    if (isCarRental) {
      for (const f of attrFields) {
        if (f.isRequired && !(attrValues[f.attributeDefinitionId] ?? '').trim()) {
          attrErrs[f.attributeDefinitionId] = t('customer.formValidation.attributeRequired', '{{name}} is required', { name: f.name });
          isValid = false;
        }
      }
    }
    setAttrErrors(attrErrs);

    if (!isValid) {
      const firstError = Object.values(errors).find((e) => e) ?? Object.values(attrErrs).find((e) => e);
      if (firstError) setError(firstError);
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});
    if (!validateForm() || !customer) return;

    setSaving(true);
    try {
      await customerService.updateCustomer(customer.id, formData);

      if (isCarRental && attrFields.length > 0) {
        await customerAttributeService.save(
          customer.id,
          'carRental',
          attrFields.map(f => ({ attributeDefinitionId: f.attributeDefinitionId, value: attrValues[f.attributeDefinitionId] ?? null })),
        );
      }

      navigate(`/customers/${customer.id}`, { state: { message: t('customer.customerUpdatedSuccess') } });
    } catch (err) {
      setError(getApiErrorMessage(err, t('customer.failedToUpdateCustomer')));
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="edit-loading-container">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!customer) {
    return (
      <Layout>
        <div className="edit-container">
          <div className="alert alert-danger">{error || t('customer.customerNotFound')}</div>
          <button className="edit-breadcrumb-btn" onClick={() => navigate('/customers')}>
            {t('customer.backToCustomers')}
          </button>
        </div>
      </Layout>
    );
  }

  const langCode = t('common.languageCode') === 'ja' ? 'ja-JP' : 'en-US';

  return (
    <Layout>
      <div className="edit-container">
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button type="button" className="ims-back-btn" onClick={() => navigate(`/customers/${id}`)}>
              <ArrowLeft size={16} strokeWidth={2} />
            </button>
            <div>
              <div className="ims-page-title">{t('customer.editTitle')}</div>
              <div className="ims-page-subtitle">{customer.name} &mdash; <code>{customer.code}</code></div>
            </div>
          </div>
          <div className="ims-top-bar-right">
            <button type="button" className="ims-btn ims-btn-secondary" onClick={() => navigate(`/customers/${id}`)} disabled={saving}>
              {t('common.cancel')}
            </button>
            <button type="submit" form="page-form" className="ims-btn ims-btn-primary" disabled={saving}>
              {saving ? <><span className="ims-btn-spinner" />{t('common.saving')}</> : <><Save size={15} strokeWidth={2} />{t('common.save')}</>}
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            <strong>{t('common.error')}:</strong> {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label={t('common.close')}
            ></button>
          </div>
        )}

        <div className="edit-content">
          <div className="edit-form-section">
            <div className="edit-card">
              <div className="edit-card-header">
                <h2 className="edit-card-title">{t('customer.customerInfo')}</h2>
              </div>
              <div className="edit-card-body">
                <form id="page-form" onSubmit={handleSubmit} noValidate>

                  {/* Customer Code — read-only */}
                  <div className="form-group">
                    <label className="form-label">{t('customer.customerCode')}</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.code}
                      disabled
                    />
                    <small className="form-helper">{t('customer.codeCannotChange')}</small>
                  </div>

                  {/* Customer Name */}
                  <div className="form-group">
                    <label className="form-label">
                      {t('customer.customerName')} <span className="required">*</span>
                      <span className="char-counter">{formData.name.length}/{MAX_LENGTHS.name}</span>
                    </label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.name ? 'is-invalid' : ''}`}
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      maxLength={MAX_LENGTHS.name}
                    />
                    {fieldErrors.name && <small className="form-error">{fieldErrors.name}</small>}
                    {!fieldErrors.name && <small className="form-helper">{t('customer.nameHint')}</small>}
                  </div>

                  {/* Contact Email */}
                  <div className="form-group">
                    <label className="form-label">
                      {t('customer.contactEmail')}
                      <span className="char-counter">{formData.contactEmail.length}/{MAX_LENGTHS.contactEmail}</span>
                    </label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.contactEmail ? 'is-invalid' : ''}`}
                      name="contactEmail"
                      value={formData.contactEmail}
                      onChange={handleInputChange}
                      maxLength={MAX_LENGTHS.contactEmail}
                    />
                    {fieldErrors.contactEmail && <small className="form-error">{fieldErrors.contactEmail}</small>}
                    {!fieldErrors.contactEmail && <small className="form-helper">{t('customer.contactEmailHint')}</small>}
                  </div>

                  {/* Contact Phone */}
                  <div className="form-group">
                    <label className="form-label">{t('customer.contactPhone')}</label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.contactPhone ? 'is-invalid' : ''}`}
                      name="contactPhone"
                      value={formData.contactPhone || ''}
                      onChange={handleInputChange}
                      maxLength={MAX_LENGTHS.contactPhone}
                    />
                    {fieldErrors.contactPhone && <small className="form-error">{fieldErrors.contactPhone}</small>}
                    {!fieldErrors.contactPhone && <small className="form-helper">{t('customer.contactPhoneHint')}</small>}
                  </div>

                  {/* Customer Type */}
                  <div className="form-group">
                    <label className="form-label">{t('customer.customerType')}</label>
                    <select
                      className="form-select"
                      name="customerType"
                      value={formData.customerType || ''}
                      onChange={handleInputChange}
                    >
                      <option value="">{t('customer.selectCustomerType')}</option>
                      <option value="Retail">{t('customer.typeRetail')}</option>
                      <option value="Wholesale">{t('customer.typeWholesale')}</option>
                      <option value="Distribution">{t('customer.typeDistribution')}</option>
                      <option value="Government">{t('customer.typeGovernment')}</option>
                      <option value="Corporate">{t('customer.typeCorporate')}</option>
                      <option value="Other">{t('customer.typeOther')}</option>
                    </select>
                  </div>

                  {/* Billing Address */}
                  <div className="form-group">
                    <label className="form-label">{t('customer.billingAddress')}</label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.billingAddress ? 'is-invalid' : ''}`}
                      name="billingAddress"
                      value={formData.billingAddress || ''}
                      onChange={handleInputChange}
                      maxLength={MAX_LENGTHS.billingAddress}
                    />
                    {fieldErrors.billingAddress && <small className="form-error">{fieldErrors.billingAddress}</small>}
                    {!fieldErrors.billingAddress && <small className="form-helper">{t('customer.billingAddressHint')}</small>}
                  </div>

                  {/* Shipping Address */}
                  <div className="form-group">
                    <label className="form-label">{t('customer.shippingAddress')}</label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.shippingAddress ? 'is-invalid' : ''}`}
                      name="shippingAddress"
                      value={formData.shippingAddress || ''}
                      onChange={handleInputChange}
                      maxLength={MAX_LENGTHS.shippingAddress}
                    />
                    {fieldErrors.shippingAddress && <small className="form-error">{fieldErrors.shippingAddress}</small>}
                    {!fieldErrors.shippingAddress && <small className="form-helper">{t('customer.shippingAddressHint')}</small>}
                  </div>

                  {/* City */}
                  <div className="form-group">
                    <label className="form-label">{t('customer.city')}</label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.city ? 'is-invalid' : ''}`}
                      name="city"
                      value={formData.city || ''}
                      onChange={handleInputChange}
                      maxLength={MAX_LENGTHS.city}
                    />
                    {fieldErrors.city && <small className="form-error">{fieldErrors.city}</small>}
                    {!fieldErrors.city && <small className="form-helper">{t('customer.cityHint')}</small>}
                  </div>

                  {/* State / Province */}
                  <div className="form-group">
                    <label className="form-label">{t('customer.state')}</label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.state ? 'is-invalid' : ''}`}
                      name="state"
                      value={formData.state || ''}
                      onChange={handleInputChange}
                      maxLength={MAX_LENGTHS.state}
                    />
                    {fieldErrors.state && <small className="form-error">{fieldErrors.state}</small>}
                    {!fieldErrors.state && <small className="form-helper">{t('customer.stateHint')}</small>}
                  </div>

                  {/* Postal Code */}
                  <div className="form-group">
                    <label className="form-label">{t('customer.postalCode')}</label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.postalCode ? 'is-invalid' : ''}`}
                      name="postalCode"
                      value={formData.postalCode || ''}
                      onChange={handleInputChange}
                      maxLength={MAX_LENGTHS.postalCode}
                    />
                    {fieldErrors.postalCode && <small className="form-error">{fieldErrors.postalCode}</small>}
                    {!fieldErrors.postalCode && <small className="form-helper">{t('customer.postalCodeHint')}</small>}
                  </div>

                  {/* Country */}
                  <div className="form-group">
                    <label className="form-label">{t('customer.country')}</label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.country ? 'is-invalid' : ''}`}
                      name="country"
                      value={formData.country || ''}
                      onChange={handleInputChange}
                      maxLength={MAX_LENGTHS.country}
                    />
                    {fieldErrors.country && <small className="form-error">{fieldErrors.country}</small>}
                    {!fieldErrors.country && <small className="form-helper">{t('customer.countryHint')}</small>}
                  </div>

                  {/* Tax ID */}
                  <div className="form-group">
                    <label className="form-label">{t('customer.taxId')}</label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.taxId ? 'is-invalid' : ''}`}
                      name="taxId"
                      value={formData.taxId || ''}
                      onChange={handleInputChange}
                      maxLength={MAX_LENGTHS.taxId}
                    />
                    {fieldErrors.taxId && <small className="form-error">{fieldErrors.taxId}</small>}
                    {!fieldErrors.taxId && <small className="form-helper">{t('customer.taxIdHint')}</small>}
                  </div>

                  {/* Credit Limit */}
                  <div className="form-group">
                    <label className="form-label">{t('customer.creditLimit')}</label>
                    <input
                      type="number"
                      className={`form-input ${fieldErrors.creditLimit ? 'is-invalid' : ''}`}
                      name="creditLimit"
                      value={formData.creditLimit ?? ''}
                      onChange={handleInputChange}
                      step="0.01"
                    />
                    {fieldErrors.creditLimit && <small className="form-error">{fieldErrors.creditLimit}</small>}
                    {!fieldErrors.creditLimit && <small className="form-helper">{t('customer.creditLimitHint')}</small>}
                  </div>

                  {/* Payment Terms */}
                  <div className="form-group">
                    <label className="form-label">{t('customer.paymentTerms')}</label>
                    <input
                      type="text"
                      className="form-input"
                      name="paymentTerms"
                      value={formData.paymentTerms || ''}
                      onChange={handleInputChange}
                    />
                  </div>

                  {/* Status */}
                  <div className="form-group">
                    <label className="form-label">{t('customer.status')}</label>
                    <select
                      className="form-select"
                      name="isActive"
                      value={formData.isActive ? 'true' : 'false'}
                      onChange={handleInputChange}
                    >
                      <option value="true">{t('customer.active')}</option>
                      <option value="false">{t('customer.inactive')}</option>
                    </select>
                  </div>

                </form>
              </div>
            </div>

            {isCarRental && attrFields.length > 0 && (
              <CustomerAttributeFields
                title={attrSetName}
                fields={attrFields}
                values={attrValues}
                onChange={handleAttrChange}
                errors={attrErrors}
              />
            )}
          </div>

          <div className="edit-info-section">
            <div className="edit-card">
              <div className="edit-card-header">
                <h3 className="edit-card-title">{t('customer.auditInformation')}</h3>
              </div>
              <div className="edit-card-body">
                <div className="info-item">
                  <span className="info-label">{t('customer.customerId')}</span>
                  <span className="info-value">{customer.id}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">{t('customer.status')}</span>
                  <span className="info-value">
                    <span className={`badge ${customer.isActive ? 'badge-active' : 'badge-inactive'}`}>
                      {customer.isActive ? t('customer.active') : t('customer.inactive')}
                    </span>
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">{t('customer.createdDate')}</span>
                  <span className="info-value">
                    {new Date(customer.createdAt).toLocaleDateString(langCode)}
                  </span>
                </div>
              </div>
            </div>

            <div className="edit-card">
              <div className="edit-card-header">
                <h3 className="edit-card-title">{t('customer.quickActions')}</h3>
              </div>
              <div className="edit-card-body">
                <button
                  type="button"
                  className="detail-quick-link"
                  onClick={() => navigate(`/customers/${customer.id}`)}
                >
                  <Eye size={16} strokeWidth={2} />
                  {t('customer.viewTitle')}
                </button>
                <button
                  type="button"
                  className="detail-quick-link"
                  onClick={() => navigate(`/sales-items?customerId=${customer.id}`)}
                  style={{ marginTop: '8px' }}
                >
                  <ShoppingBag size={16} strokeWidth={2} />
                  {t('customer.viewSalesItems')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CustomerEditPage;
